export const PLACEMENT_SALARY = 'jobdiva.submittal.placementSalary';
export const FEE_MODE = 'jobdiva.submittal.feeCalculationMode';
export const FEE_AMOUNT = 'jobdiva.submittal.feeAmount';
export const FEE_PERCENT = 'jobdiva.submittal.feePercent';
export const HOURS_PER_WEEK = 'jobdiva.submittal.hoursPerWeek';
export const FEE_PERCENT_UNIT = 'jobdiva.submittal.feePercentUnit';
export const PLACEMENT_DERIVED_PATHS = [PLACEMENT_SALARY, HOURS_PER_WEEK, FEE_PERCENT, FEE_PERCENT_UNIT, FEE_AMOUNT];
export const getValue = (root, path) => String(path).replace(/\[(\d+)\]/g, '.$1').split('.').reduce((value, key) => value?.[key], root);
export const putValue = (root, path, value) => {
  const keys = path.split('.');
  let target = root;
  keys.slice(0, -1).forEach(key => { target = target[key] ||= {}; });
  target[keys.at(-1)] = value;
};
const compact = value => String(value || '').toLowerCase().replace(/[^a-z]/g, '');
export const rateKind = field => {
  const names = [compact(field.sourcePath?.split('.').at(-1)), compact(field.label)];
  return ['otBill', 'otPay', 'bill', 'pay'].find(kind => {
    const suffixes = { otBill: ['otbillrate', 'overtimebillrate'], otPay: ['otpayrate', 'overtimepayrate'], bill: ['billrate'], pay: ['payrate'] }[kind];
    return names.some(name => suffixes.some(suffix => name.endsWith(suffix)));
  }) || null;
};
export const resolveRates = fields => Object.fromEntries(['bill', 'otBill', 'pay', 'otPay'].map(kind => {
  const matches = fields.filter(field => field.isActive !== false && rateKind(field) === kind);
  if (matches.length > 1) throw new Error(`Multiple active ${kind} rate fields are configured. Resolve the duplicate mapping.`);
  return [kind, matches[0]?.sourcePath || { bill: 'jobdiva.job.billRate', otBill: 'jobdiva.job.otBillRate', pay: 'assignment.payRate', otPay: 'jobdiva.job.otPayRate' }[kind]];
}));
export const isPermanentPlacement = root => String(getValue(root, 'ncf.submissionReason') || '').trim().toLowerCase() === 'new perm hire';
export const ratesLayout = (sections, permanent) => {
  const rateFields = ['bill', 'otBill', 'pay', 'otPay'].flatMap(kind => sections.flatMap(section => section.fields).filter(field => rateKind(field) === kind));
  const host = sections.find(section => section.fields.some(field => rateKind(field)))?.title
    || sections.find(section => section.title === 'JobDiva Submittal')?.title;
  const submittalFields = permanent ? sections.filter(section => section.title === 'JobDiva Submittal').flatMap(section => section.fields) : [];
  return sections.map(section => ({
    ...section,
    rateFields: permanent ? section.title === host ? rateFields : [] : ['bill', 'otBill', 'pay', 'otPay'].flatMap(kind => section.fields.filter(field => rateKind(field) === kind)),
    otherFields: section.fields.filter(field => !rateKind(field) && !(permanent && section.title === 'JobDiva Submittal')
      && (permanent || ![PLACEMENT_SALARY, HOURS_PER_WEEK, FEE_MODE].includes(field.sourcePath))),
    submittalFields: section.title === host ? submittalFields : [],
    isRatesHost: section.title === host
  })).filter(section => section.isRatesHost || section.rateFields.length > 0 || section.otherFields.length > 0);
};
export const financialPaths = fields => [...new Set([
  ...Object.values(resolveRates(fields)), 'jobdiva.job.billRatePer', 'assignment.payRateType',
  ...fields.filter(field => field.isActive !== false && /fee|salary/i.test(`${field.sourcePath} ${field.label}`) && ['currency', 'number'].includes(field.format)).map(field => field.sourcePath),
  PLACEMENT_SALARY, HOURS_PER_WEEK, FEE_MODE, FEE_AMOUNT, FEE_PERCENT, 'jobdiva.submittal.feeType', 'jobdiva.submittal.conversionFeeAmount'
])].sort();
export const financialSignature = (root, fields) => JSON.stringify(financialPaths(fields).map(path => [path, String(getValue(root, path) ?? '').trim()]));
export const calculatePlacementFee = (salary, percent) => {
  const annualSalary = Number(salary);
  const percentage = Number(percent);
  if (String(salary ?? '').trim() === '' || !Number.isFinite(annualSalary) || annualSalary <= 0) throw new Error('Enter a positive annual placement salary.');
  if (String(percent ?? '').trim() === '' || !Number.isFinite(percentage) || percentage < 0 || percentage > 100) throw new Error('Enter a fee percentage between 0 and 100.');
  return Math.round((annualSalary * percentage / 100 + Number.EPSILON) * 100) / 100;
};
export const placementPayBasis = (root, fields = []) => {
  const payPath = resolveRates(fields).pay;
  const rate = Number(String(getValue(root, payPath) ?? '').replace(/[$,]/g, '').trim());
  const frequency = String(getValue(root, 'assignment.payRateType') || '').trim().toLowerCase();
  const hourly = ['hour', 'hourly', 'hr', 'h', 'per hour'].includes(frequency);
  const annual = ['year', 'yearly', 'annual', 'annually', 'salary', 'y', 'per year'].includes(frequency);
  return { rate, hasPay: Number.isFinite(rate) && rate > 0, hourly, annual, frequency };
};
export const synchronizePlacementInputs = (root, fields = []) => {
  if (!isPermanentPlacement(root)) return root;
  const raw = getValue(root, 'jobdiva.submittal.raw');
  const importedPercent = raw && Object.entries(raw).find(([key]) => compact(key) === 'feepercent')?.[1];
  const percent = getValue(root, FEE_PERCENT);
  if (getValue(root, FEE_PERCENT_UNIT) !== 'percent' && importedPercent != null
    && String(percent).trim() === String(importedPercent).trim()) {
    const numeric = Number(importedPercent);
    if (Number.isFinite(numeric) && numeric >= 0 && numeric <= 1) {
      putValue(root, FEE_PERCENT, Math.round(numeric * 100 * 1e8) / 1e8);
      putValue(root, FEE_PERCENT_UNIT, 'percent');
    }
  }
  const basis = placementPayBasis(root, fields);
  if (basis.hasPay) {
    let salary;
    if (basis.hourly) {
      const hours = getValue(root, HOURS_PER_WEEK) ?? 40;
      putValue(root, HOURS_PER_WEEK, hours);
      const numericHours = Number(hours);
      salary = String(hours).trim() !== '' && Number.isFinite(numericHours) && numericHours > 0 && numericHours <= 168 ? basis.rate * numericHours * 52 : '';
    } else if (basis.annual) salary = basis.rate;
    else if (['week', 'weekly', 'per week'].includes(basis.frequency)) salary = basis.rate * 52;
    else if (['month', 'monthly', 'per month'].includes(basis.frequency)) salary = basis.rate * 12;
    else salary = '';
    putValue(root, PLACEMENT_SALARY, salary === '' ? '' : Math.round((salary + Number.EPSILON) * 100) / 100);
  }
  return root;
};
export const applyPlacementCalculation = (root, fields = []) => {
  if (!isPermanentPlacement(root)) return root;
  synchronizePlacementInputs(root, fields);
  const mode = getValue(root, FEE_MODE);
  if (mode === 'percentage') putValue(root, FEE_AMOUNT, calculatePlacementFee(getValue(root, PLACEMENT_SALARY), getValue(root, FEE_PERCENT)));
  else if (mode === 'flat') {
    const amount = getValue(root, FEE_AMOUNT);
    if (String(amount ?? '').trim() === '' || !Number.isFinite(Number(amount)) || Number(amount) < 0) throw new Error('Enter a valid flat placement fee.');
  } else if (mode && mode !== 'imported') throw new Error('Select a valid placement fee calculation mode.');
  return root;
};
export const assertFinancialReview = (root, fields, review) => {
  if (review?.signature !== financialSignature(root, fields)) {
    const error = new Error('Review and acknowledge the current rates, frequencies and placement fees before submitting to HR.');
    error.statusCode = 400;
    error.code = 'NCF_RATES_REVIEW_REQUIRED';
    throw error;
  }
};